import {ChangeEventHandler, FC, FormEventHandler, useContext, useEffect, useRef, useState} from "react";
import {motion} from "framer-motion";
import Heading from "../components/ui/Heading";
import Card from "../components/ui/Card";
import useApi from "../hooks/useApi";
import {IGroup} from "../types/ApiTypes";
import Table from "../components/ui/Table";
import {AppContext} from "../context/AppContext";
import Avatar from "../components/ui/Avatar";
import Pagination from "../components/ui/Pagination";
import Loading from "../components/Loading";
import {CgSearch} from "react-icons/cg";
import Select, {onSelectChangeHandler} from "../components/ui/Select";
import {useAccount} from "@azure/msal-react";
import {Link} from "react-router-dom";
import {FaSortDown, FaSortUp, FaTimes} from "react-icons/fa";
import axios from "axios";

const SEARCH_SELECT_OPTIONS = [
    {label: "Name", value: "name"},
    {label: "Besitzer", value: "owner"},
]

const HomePage: FC = () => {
    const mounted = useRef(false);
    const searchInputRef = useRef<HTMLInputElement|null>(null);
    const [loading, setLoading] = useState(false);
    const {fetchGroups} = useApi();
    const [spaces, setSpaces] = useState<IGroup[]>([]);
    // const [count, setCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pages, setPages] = useState(1);
    const {state: {user: users}} = useContext(AppContext);

    const account = useAccount();

    const [searchText, setSearchText] = useState('');
    const [searchFilter, setSearchFilter] = useState<'name'|'owner'|undefined>('name');
    const [searchSort, setSearchSort] = useState<"ASC"|"DESC">('DESC');

    useEffect(() => {
        mounted.current = true;
        return () => {mounted.current = false}
    })

    useEffect(() => {
        setLoading(true);
        fetchGroups(1,'ASC').then(res => {
            if(res && mounted.current) {
                setSpaces(res.spaces);
                // setCount(res.count);
                setPages(res.pages||1);
                setCurrentPage(res.page||1);
                setLoading(false);
            }
        })
    },[fetchGroups])

    const onInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setSearchText(e.target.value);
    }

    const onPageSelected = (page:number) => {
        setLoading(true);
        fetchGroups(page,searchSort,false,searchText,searchFilter).then(res => {
            if(res && mounted.current) {
                setSpaces(res.spaces);
                // setCount(res.count);
                setPages(res.pages||1);
                setCurrentPage(res.page||1);
                window.scroll({
                    top: 0,
                    behavior: "smooth"
                });
                setLoading(false);
            }
        })
    }

    const onFilterChange: onSelectChangeHandler<string> = (change) => {
        setSearchFilter(change.value as 'name'|'owner');
    }

    const onFilterReset = () => {
        setSearchFilter(undefined);
        setSearchText('');
        if(searchInputRef.current) searchInputRef.current.value = '';
        onPageSelected(1);
    }

    const onSortChange = () => {
        setLoading(true);
        setSearchSort(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        fetchGroups(currentPage,searchSort,false,searchText,searchFilter).then(res => {
            if(res && mounted.current) {
                setSpaces(res.spaces);
                // setCount(res.count);
                setPages(res.pages||1);
                setCurrentPage(res.page||1);
                window.scroll({
                    top: 0,
                    behavior: "smooth"
                });
                setLoading(false);
            }
        })
    }

    const onSearch: FormEventHandler = (e) => {
        setLoading(true)
        fetchGroups(1,searchSort,false,searchText,searchFilter).then(res => {
            if(res && mounted.current) {
                setSpaces(res.spaces);
                // setCount(res.count);
                setPages(res.pages||1);
                setCurrentPage(res.page||1);
                window.scroll({
                    top: 0,
                    behavior: "smooth"
                });
            }
        }).catch(e => {
            if(axios.isAxiosError(e)) {
                if(e.response?.status === 404) {
                    setSpaces([]);
                }
            }
        }).finally(() => {
            setLoading(false);
        })
        e.preventDefault();
    }

    return (
        <motion.section initial={{
            opacity: 0
        }} exit={{
            opacity: 0
        }} animate={{
            opacity: 1
        }}>
            <Heading>Verfügbaren Spaces</Heading>
            <Card>
                <div className='flex flex-col lg:flex-row justify-between items-center mb-2 gap-4'>
                    {pages > 1 && <Pagination currentPage={currentPage} pages={pages} onPageSelected={onPageSelected}/>}
                    <form className='dark:bg-mainGray bg-transparent shadow dark:border border-slate-700 rounded-md shadow-md flex items-center p-2 space-x-2 flex-1' onSubmit={onSearch}>
                        <CgSearch/>
                        <input ref={searchInputRef} className='bg-transparent flex-1 outline-none text-sm' placeholder={searchFilter === 'name' ? 'Suche nach einem Namen des Spaces' : 'Suche nach dem Besitzer'} type="text" onChange={onInputChange}/>
                        <Select options={SEARCH_SELECT_OPTIONS} small onChange={onFilterChange} defaultValue={SEARCH_SELECT_OPTIONS[0]}/>
                        <FaTimes onClick={onFilterReset} className='text-error-brighter cursor-pointer'/>
                    </form>
                </div>
                <Table.Skeleton>
                    <Table.THead>
                        <Table.HeadTR>
                            <Table.TH onClick={onSortChange}>
                                <div className='cursor-pointer flex space-x-2 items-center'>
                                    Name
                                    {searchSort === 'DESC' && <FaSortUp className='text-success-darker'/>}
                                    {searchSort === 'ASC' && <FaSortDown className='text-error-brighter'/>}
                                </div>
                            </Table.TH>
                            <Table.TH>Bereich (intern)</Table.TH>
                            <Table.TH>Besitzer</Table.TH>
                            <Table.TH>Stellvertreter</Table.TH>
                        </Table.HeadTR>
                    </Table.THead>
                    <Table.TBody>
                        {!loading && spaces.map(x => (
                            <Table.BodyTR key={x.requestId}>
                                <Table.TD>
                                    <Link className='dark:text-primary text-blue-700' to={`/space/${x.requestId}`}>{x.type} {x.name}</Link>
                                </Table.TD>
                                <Table.TD>{x.unit?.name}</Table.TD>
                                <Table.TD>
                                    <div className='flex items-center gap-2'>
                                        <Avatar inline seed={x.primary_owner}/> {users.find(y => y.id === x.primary_owner)?.displayName || <span>Invalid user: {x.primary_owner}</span>}
                                    </div>
                                </Table.TD>
                                <Table.TD>
                                    <div className='flex items-center gap-2'>
                                        <Avatar inline seed={x.secondary_owner}/> {users.find(y => y.id === x.secondary_owner)?.displayName || <span>Invalid User: {x.secondary_owner}</span>}
                                    </div>
                                </Table.TD>
                            </Table.BodyTR>
                        ))}
                        {loading && <Table.BodyTR>
                            <Table.TD colspan={4}>
                                <Loading small/>
                            </Table.TD>
                        </Table.BodyTR>}
                        {spaces.length === 0 && !loading && <Table.BodyTR>
                            <Table.TD colspan={4}>
                                Keine Spaces wurden gefunden
                            </Table.TD>
                        </Table.BodyTR>}
                    </Table.TBody>
                </Table.Skeleton>
                {pages > 1 && <Pagination className='mt-2' currentPage={currentPage} pages={pages} onPageSelected={onPageSelected}/>}
            </Card>
        </motion.section>
    );
};

export default HomePage;